/**
 * The engine binding — one call site for the whole app. Binds @momo/i18n's
 * de/en catalogs to schmancy's generic localization engine so LocalizeMixin,
 * setLocale, and locale$ all share the one schmancy state() singleton, and
 * every consumer — a view via the mixin, or non-component code via
 * uiLanguage() — reads the same UI-language state.
 *
 * Both halves of the app's wording hang off that one state: this repo's own
 * catalogs through the mixin's t(), and the words schmancy's components render
 * on their own behalf through setComponentText, below.
 */
import {
	type ComponentTextOverrides,
	createLocalization,
	setComponentText,
} from "@mhmo91/schmancy";
import { componentTextDe, de, en, type Locale } from "@momo/i18n";
import { Subject } from "rxjs";

const { LocalizeMixin, setLocale, locale$ } = createLocalization(
	{ de, en },
	{ de: "de-DE", en: "en-GB" },
	{ defaultLocale: "de" },
);

// <schmancy-lightbox>'s fourteen keys. Their home is componentTextDe beside the
// other hundred-odd — one table, one file — and they fold back into it as a
// move, not a rewrite; they are spread in here for the override check below.
// Like every key that table shares between the Sie admin
// and the Du storefront, none of the fourteen addresses the reader.
const lightboxDe = {
	// Both are fallbacks the consumer's own naming preempts: `viewer` names the
	// overlay when no label was given, `image` names the picture inside it, and
	// this deployment's one caller passes the event's name for both.
	"lightbox.viewer": "Bildansicht",
	"lightbox.image": "Bild",
	// Subject first — "Lädt {{label}}" carries the English word order into a
	// German sentence that has its own.
	"lightbox.loading": "{{label}} wird geladen",
	"lightbox.unavailable": "Bild nicht verfügbar",
	// Object first, verb as a trailing infinitive, and naming the surface it
	// closes rather than the act alone.
	"lightbox.close": "Bildansicht schließen",
	"lightbox.previous": "Vorheriges Bild",
	"lightbox.next": "Nächstes Bild",
	// The poster that opens this viewer is already labelled "Plakat vergrößern"
	// in the storefront, so the buttons inside it keep that verb rather than
	// minting a second German word for the same act.
	"lightbox.zoomOut": "Verkleinern",
	"lightbox.zoomIn": "Vergrößern",
	// Names the zoom and not the view, though the control recentres a panned
	// image too: "Ansicht zurücksetzen" inside a viewer called "Bildansicht"
	// reads as resetting the viewer itself.
	"lightbox.resetZoom": "Zoom zurücksetzen",
	// "X von Y" is what this deployment already counts with, on the ticket
	// sequence and the reorder announcement alike. The first is read beside the
	// zoom percentage, the second only spoken.
	"lightbox.position": "{{current}} von {{total}}",
	"lightbox.announce": "{{label}}, {{current}} von {{total}}",
	// Spoken only, once a zoom gesture settles. The space before the percent
	// sign is what every other number this deployment renders takes.
	"lightbox.zoomLevel": "{{percent}} % Zoom",
	// Read once, and the only place the gestures are named at all — a pointer
	// user discovers pinch and double-tap by trying them, where a screen-reader
	// user has nothing to try. Bare infinitives, like every other line in this
	// table: the viewer opens over a buyer's poster and over a staff artwork
	// alike, so no sentence here may pick Du or Sie.
	"lightbox.instructions":
		"Zum Zoomen zwei Finger auseinander- oder zusammenziehen oder Plus und Minus drücken, mit einem Doppeltippen den Zoom umschalten, im gezoomten Bild durch Ziehen den Ausschnitt verschieben, zum Blättern wischen oder die Pfeiltasten benutzen, zum Schließen nach unten wischen oder Escape drücken.",
} satisfies ComponentTextOverrides;

// schmancy's own chrome — date-range's preset chips, its Von/Bis labels, its
// aria descriptions and its four rendered validation messages — is one table
// the library swaps whole. It follows locale$ rather than being set once at
// boot, because a language switch has to reach chrome that is already on
// screen, including a picker panel the overlay currently has open. `{}` is not
// "no override": it is the call that restores the library's own English, which
// is why there is no `componentTextEn` to pair with the German.
//
// The `satisfies` checks the override keys. @momo/i18n carries no schmancy
// dependency — it is the catalog both the browser and the functions workspace
// read — so this seam checks the catalog against the library's vocabulary.
// Untranslated keys use the library's English defaults.
locale$.subscribe((lang) =>
	setComponentText(
		lang === "de"
			? ({
					...componentTextDe,
					...lightboxDe,
					"navigationBar.hasNotifications": "Neue Benachrichtigungen",
				} satisfies ComponentTextOverrides)
			: {},
	),
);

// Bare setLocale flips the engine WITHOUT persisting — the boot fallback and
// the account-value application use it so only an explicit user choice
// (setUiLanguage) ever writes the device key.
export { LocalizeMixin, locale$, setLocale };

const STORAGE_KEY = "momo/language";

/** Fires only on an explicit setUiLanguage call — never the boot-time
 *  fallback (bare setLocale) or the account-value reconciliation
 *  (session.sync.ts's bare setLocale) — so web's settings.sync.ts can
 *  promote a staff member's own pick to their Firestore user doc without
 *  also re-writing it on every account-driven locale application. */
export const languageChosen$ = new Subject<Locale>();

/** setLocale plus the device-tier persistence a buyer's device carries
 *  across reloads with no account of their own. */
export function setUiLanguage(lang: Locale): void {
	setLocale(lang);
	localStorage.setItem(STORAGE_KEY, lang);
	languageChosen$.next(lang);
}

/** The current UI language for a non-component call site (a .sync toast,
 *  an email-locale default) that can't read LocalizeMixin's own
 *  this.uiLanguage instance getter. locale$ is backed by a schmancy
 *  state() singleton, so subscribe replays the current value synchronously. */
export function uiLanguage(): Locale {
	let current: Locale = "de";
	locale$
		.subscribe((lang) => {
			current = lang;
		})
		.unsubscribe();
	return current;
}
